import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function SiteHeader() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    setMenuOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? 'border-background-200 bg-background-50/95 backdrop-blur'
          : 'border-transparent bg-background-50'
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2 cursor-pointer">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-book-2-line text-lg" />
          </span>
          <span className="text-lg font-semibold tracking-tight text-foreground-950">
            韩漫网址
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          <Link
            to="/"
            className="rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
          >
            首页
          </Link>
          <div className="relative" ref={catRef}>
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className="flex items-center gap-1 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
            >
              分类
              <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`} />
            </button>
            {catOpen && (
              <div className="absolute left-0 top-full mt-2 grid w-[420px] grid-cols-3 gap-1 rounded-lg border border-background-200 bg-background-50 p-3 animate-fade-in">
                {categories.map((c) => (
                  <Link
                    key={c.key}
                    to={`/category/${c.key}`}
                    onClick={() => setCatOpen(false)}
                    className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                  >
                    <i className={`${c.icon} text-primary-500`} />
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <Link
            to="/category/all"
            className="rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
          >
            全部漫画
          </Link>
          <Link
            to="/about"
            className="rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
          >
            关于我们
          </Link>
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden md:block">
          <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 focus-within:border-primary-400">
            <i className="ri-search-line text-sm text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索漫画名称或作者"
              className="w-40 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none lg:w-56"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              搜索
            </button>
          </div>
        </form>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-800 md:hidden"
          aria-label="打开菜单"
        >
          <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-3 md:hidden">
          <form onSubmit={submitSearch} className="mb-3">
            <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2">
              <i className="ri-search-line text-sm text-foreground-400" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画名称或作者"
                className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
              <button type="submit" className="text-sm font-medium text-primary-600">
                搜索
              </button>
            </div>
          </form>
          <div className="grid grid-cols-4 gap-2">
            {categories.map((c) => (
              <Link
                key={c.key}
                to={`/category/${c.key}`}
                onClick={() => setMenuOpen(false)}
                className="flex flex-col items-center gap-1 rounded-md border border-background-200 bg-background-50 py-2.5 text-xs text-foreground-700"
              >
                <i className={`${c.icon} text-base text-primary-500`} />
                {c.name}
              </Link>
            ))}
          </div>
          <div className="mt-3 flex flex-col gap-1">
            <Link to="/" onClick={() => setMenuOpen(false)} className="rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100">
              首页
            </Link>
            <Link to="/category/all" onClick={() => setMenuOpen(false)} className="rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100">
              全部漫画
            </Link>
            <Link to="/about" onClick={() => setMenuOpen(false)} className="rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100">
              关于我们
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}